"use client";

import React, { useState, useMemo, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Button } from "@/components/shadcn/button";
import { Badge } from "@/components/shadcn/badge";
import { Skeleton } from "@/components/shadcn/skeleton";
import { Input } from "@/components/shadcn/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import {
  Terminal,
  AlertCircle,
  AlertTriangle,
  Info,
  Bug,
  Search,
  Download,
  LifeBuoy,
  RefreshCw,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useSpotUser } from "@/utils/spotUser";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import { usePermissions } from "@/hooks/usePermissions";
import { useFeatureFlag } from "@/services/FeatureFlags/FeatureFlags";
import { P } from "@/constants/permissions";
import { SendLogsToSupportDialog } from "./SendLogsToSupportDialog";
import { SpotToast } from "@/components/SpotToast";

interface ExecutionLogEntry {
  id?: string;
  ts: string;
  level: string;
  msg: string;
  name?: string;
  func?: string;
  line?: string;
  exc_type?: string | null;
  exc_msg?: string | null;
  exc_tb?: string | null;
}

interface ExecutionLogsResponse {
  run_id: string;
  group_id: string;
  source: string;
  log_count: number;
  logs: ExecutionLogEntry[];
  archived_at?: string | null;
}

const LOG_LEVEL_CONFIG: Record<
  string,
  { icon: typeof Info; color: string; bgColor: string }
> = {
  DEBUG: { icon: Bug, color: "text-muted-foreground", bgColor: "bg-muted/50" },
  INFO: {
    icon: Info,
    color: "text-blue-600 dark:text-blue-400",
    bgColor: "bg-blue-500/10",
  },
  WARNING: {
    icon: AlertTriangle,
    color: "text-yellow-600 dark:text-yellow-400",
    bgColor: "bg-yellow-500/10",
  },
  ERROR: {
    icon: AlertCircle,
    color: "text-red-600 dark:text-red-400",
    bgColor: "bg-red-500/10",
  },
  CRITICAL: {
    icon: AlertCircle,
    color: "text-red-700 dark:text-red-300",
    bgColor: "bg-red-600/20",
  },
};

const LogEntry = React.memo(({ log }: { log: ExecutionLogEntry }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const config = LOG_LEVEL_CONFIG[log.level] || LOG_LEVEL_CONFIG.INFO;
  const Icon = config.icon;
  const hasException = log.exc_type || log.exc_msg || log.exc_tb;

  const timestamp = new Date(log.ts).toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
  });

  return (
    <div
      data-testid="log-entry"
      data-log-level={log.level}
      className={cn(
        "group px-3 py-2 border-b border-border/50 hover:bg-accent/50 transition-colors",
        hasException && "cursor-pointer"
      )}
      onClick={() => hasException && setIsExpanded(!isExpanded)}
    >
      <div className="flex items-start gap-2">
        <Icon className={cn("h-4 w-4 mt-0.5 flex-shrink-0", config.color)} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs font-mono text-muted-foreground">
              {timestamp}
            </span>
            <Badge
              variant="outline"
              className={cn(
                "text-[10px] px-1.5 py-0 h-4 font-mono",
                config.bgColor
              )}
            >
              {log.level}
            </Badge>
            {log.name && (
              <span className="text-xs text-muted-foreground truncate">
                {log.name}
                {log.func && `:${log.func}`}
                {log.line && `:${log.line}`}
              </span>
            )}
          </div>
          <p className="text-sm font-mono whitespace-pre-wrap break-all">
            {log.msg}
          </p>
          {hasException && (
            <div className="mt-1 flex items-center gap-1">
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                {log.exc_type}
              </Badge>
              {log.exc_msg && (
                <span className="text-xs text-red-600 dark:text-red-400 truncate">
                  {log.exc_msg}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {isExpanded && log.exc_tb && (
        <div className="mt-2 ml-6 p-2 bg-destructive/10 rounded-md border border-destructive/20">
          <pre className="text-xs font-mono text-destructive whitespace-pre-wrap overflow-x-auto">
            {log.exc_tb}
          </pre>
        </div>
      )}
    </div>
  );
});

LogEntry.displayName = "LogEntry";

// Default levels to show (excluding DEBUG)
const DEFAULT_VISIBLE_LEVELS = new Set([
  "INFO",
  "WARNING",
  "ERROR",
  "CRITICAL",
]);

type DataSource = "auto" | "hot" | "cold";

const DATA_SOURCE_OPTIONS: { value: DataSource; label: string }[] = [
  { value: "auto", label: "Auto" },
  { value: "hot", label: "Redis" },
  { value: "cold", label: "S3" },
];

const getSourceLabel = (source: string): string => {
  switch (source) {
    case "hot":
      return "Redis";
    case "cold":
      return "S3";
    case "memory":
      return "Cache";
    case "not_found":
      return "Not found";
    default:
      return source;
  }
};

export const LogsTab = ({
  runId,
  fetchUrl,
  canView,
}: {
  runId?: string;
  fetchUrl?: string;
  canView?: boolean;
}) => {
  const { spotGroup, spotUser, isOnPrem } = useSpotUser();
  const canViewLogs = canView ?? (spotUser.is_admin || isOnPrem);
  const { hasPermission } = usePermissions();
  const { value: supportLogExportEnabled } = useFeatureFlag(
    "SUPPORT_LOG_EXPORT_ENABLED"
  );
  const canSendToSupport =
    !!runId && supportLogExportEnabled && hasPermission(P.LOGS_SEND_TO_SUPPORT);
  const [sendToSupportOpen, setSendToSupportOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [forceSource, setForceSource] = useState<DataSource>("auto");
  const [sourceSelectOpen, setSourceSelectOpen] = useState(false);
  // Inclusive multi-select filtering - default to showing INFO, WARNING, ERROR, CRITICAL (hiding DEBUG)
  const [selectedLevels, setSelectedLevels] = useState<Set<string>>(
    () => new Set(DEFAULT_VISIBLE_LEVELS)
  );

  const parentRef = useRef<HTMLDivElement>(null);

  const {
    data: logsData,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery<ExecutionLogsResponse>({
    queryKey: ["execution-logs", runId, forceSource],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (forceSource !== "auto") {
        params.set("force_source", forceSource);
      }
      const queryString = params.toString();
      const baseUrl =
        fetchUrl ||
        `/api/backend/groups/${spotGroup.id}/runs/${runId}/execution-logs`;
      const url = `${baseUrl}${queryString ? `?${queryString}` : ""}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch logs");
      return response.json();
    },
    enabled: !!(fetchUrl || runId) && !!spotGroup.id,
    staleTime: 30000,
  });

  const logs = logsData?.logs;

  const filteredLogs = useMemo(() => {
    if (!logs) return [];
    return logs?.filter((log) => {
      const matchesSearch =
        !searchQuery ||
        log.msg.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.exc_msg?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesLevel = selectedLevels.has(log.level);
      return matchesSearch && matchesLevel;
    });
  }, [logs, searchQuery, selectedLevels]);

  const logLevelCounts = useMemo(() => {
    if (!logs) return {};
    return logs?.reduce(
      (acc, log) => {
        acc[log.level] = (acc[log.level] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [logs]);

  const toggleLevel = useCallback((level: string) => {
    setSelectedLevels((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(level)) {
        newSet.delete(level);
      } else {
        newSet.add(level);
      }
      return newSet;
    });
  }, []);

  // Virtualizer for efficient rendering of large log lists
  const virtualizer = useVirtualizer({
    count: filteredLogs.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60,
    // I tested higher overscans but honestly even with very fast scrolling 10 was enough
    overscan: 10,
  });

  const [isDownloadingBundle, setIsDownloadingBundle] = useState(false);

  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadLogs = () => {
    if (!logsData) return;
    const content = logsData.logs
      .map(
        (log) =>
          `[${log.ts}] [${log.level}] ${log.name || ""}:${log.func || ""}:${log.line || ""} - ${log.msg}${log.exc_tb ? `\n${log.exc_tb}` : ""}`
      )
      .join("\n");
    triggerBlobDownload(
      new Blob([content], { type: "text/plain" }),
      "execution-logs.txt"
    );
  };

  const handleDownloadBundle = async () => {
    if (!runId) return;
    setIsDownloadingBundle(true);
    try {
      const response = await fetch(
        `/api/backend/groups/${spotGroup.id}/runs/${runId}/debug-export`
      );
      if (!response.ok) throw new Error("Failed to export run");
      const blob = await response.blob();
      triggerBlobDownload(blob, `run-debug-${runId}.zip`);
    } catch {
      SpotToast.error("Failed to download the run debug bundle");
    } finally {
      setIsDownloadingBundle(false);
    }
  };

  if (!canViewLogs) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <NoxusEmptyBlock
          size="sm"
          icon={Terminal}
          title="Access restricted"
          description="Execution logs are only available to platform administrators."
        />
      </div>
    );
  }

  if (!runId && !fetchUrl) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <NoxusEmptyBlock
          size="sm"
          icon={Terminal}
          title="No run selected"
          description="Run a workflow to see execution logs here."
        />
      </div>
    );
  }

  return (
    <div data-testid="logs-tab" className="flex flex-col h-full w-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          {logsData && (
            <span data-testid="log-count">{logsData.log_count} entries</span>
          )}
          <div className="flex items-center gap-1.5">
            <span className="text-xs">Source:</span>
            <Select
              value={forceSource}
              onValueChange={(value) => setForceSource(value as DataSource)}
              open={sourceSelectOpen}
              onOpenChange={setSourceSelectOpen}
            >
              <SelectTrigger
                data-testid="log-source"
                className="h-6 w-[70px] text-xs px-2 gap-1"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent sideOffset={4}>
                {DATA_SOURCE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {logsData && logsData.source !== "not_found" && (
              <Badge data-testid="log-source-badge" variant="outline">
                {getSourceLabel(logsData.source)}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="iconsm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={cn(isFetching && "animate-spin")} />
          </Button>
          {runId ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="log-download"
                  variant="ghost"
                  size="iconsm"
                  title="Download"
                >
                  <Download className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={handleDownloadLogs}
                  disabled={!logsData?.logs?.length}
                >
                  Download logs
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleDownloadBundle}
                  disabled={isDownloadingBundle}
                  title="Bundles the flow and this run's logs to attach when reporting an issue"
                >
                  Download diagnostic bundle
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="ghost"
              size="iconsm"
              onClick={handleDownloadLogs}
              disabled={!logsData?.logs?.length}
              title="Download logs"
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
          )}
          {canSendToSupport && (
            <Button
              data-testid="log-send-support"
              variant="ghost"
              size="iconsm"
              onClick={() => setSendToSupportOpen(true)}
              title="Send logs to support"
            >
              <LifeBuoy className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      {canSendToSupport && runId && (
        <SendLogsToSupportDialog
          isOpen={sendToSupportOpen}
          onOpenChange={setSendToSupportOpen}
          groupId={spotGroup.id}
          runId={runId}
        />
      )}

      {/* Filters */}
      <div className="px-4 py-4 border-b flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            data-testid="log-search"
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-7 text-sm"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-5 w-5"
              onClick={() => setSearchQuery("")}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
        <div className="flex gap-1 flex-wrap">
          {Object.keys(LOG_LEVEL_CONFIG).map((level) => {
            const config = LOG_LEVEL_CONFIG[level];
            const count = logLevelCounts[level] || 0;
            const isSelected = selectedLevels.has(level);
            return (
              <Button
                key={level}
                data-testid="log-level-filter"
                data-level={level}
                data-selected={isSelected}
                variant={isSelected ? "secondary" : "ghost"}
                size="sm"
                className={cn(
                  "h-6 px-2 text-xs transition-opacity",
                  !isSelected && "opacity-50"
                )}
                onClick={() => toggleLevel(level)}
              >
                <span className={config.color}>{level}</span>
                <Badge variant="outline" className="ml-1 h-4 px-1 text-[10px]">
                  {count}
                </Badge>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Logs - Virtualized for performance */}
      <div ref={parentRef} className="flex-1 min-h-0 overflow-auto">
        {isLoading ? (
          <div className="p-4 space-y-2">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex items-start gap-2">
                <Skeleton className="h-4 w-4 mt-0.5" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="p-8">
            <NoxusEmptyBlock
              size="sm"
              icon={AlertCircle}
              title="Failed to load logs"
              description="There was an error loading the execution logs."
              button="Retry"
              onClick={() => refetch()}
            />
          </div>
        ) : !logsData?.logs?.length ? (
          <div className="p-8 h-full flex items-center justify-center">
            <NoxusEmptyBlock
              size="sm"
              icon={Terminal}
              title={
                forceSource !== "auto"
                  ? "Logs not available in this source"
                  : "No logs available"
              }
              description={
                forceSource !== "auto"
                  ? `No logs found in ${forceSource === "hot" ? "Redis" : "S3"}. Try selecting a different source or "Auto" to search all sources.`
                  : "No execution logs were captured for this run. Logs are available for runs completed in the last 7 days."
              }
            />
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="p-8 h-full flex items-center justify-center">
            <NoxusEmptyBlock
              size="sm"
              icon={Search}
              title="No matching logs"
              description="No logs match your search criteria."
              button="Clear filters"
              onClick={() => {
                setSearchQuery("");
                setSelectedLevels(new Set(DEFAULT_VISIBLE_LEVELS));
              }}
            />
          </div>
        ) : (
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const log = filteredLogs[virtualRow.index];
              return (
                <div
                  key={log.id || virtualRow.index}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                >
                  <LogEntry log={log} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
